/**
 * Shared hooks used across the app.
 *
 * useCountUp — number ticker animation
 * useShortcuts — global keyboard shortcuts with input-focus opt-out
 * useDealWatcher — detect new L7 customers and trigger confetti+toast+gold row
 */
import { useEffect, useRef, useState } from 'react';
import { fireConfetti } from './confetti';
import { toast } from './toast';

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Animate a number from 0 (or prevTarget) to target over `duration` ms.
 * Uses requestAnimationFrame + easeOutCubic, not CSS — values are real numbers.
 * Returns the current animated value to render in JSX.
 */
export function useCountUp(target: number, duration = 700): number {
  const [value, setValue] = useState(typeof target === 'number' ? target : 0);
  const prev = useRef<number>(0);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    if (typeof target !== 'number' || !isFinite(target)) {
      setValue(target);
      return;
    }
    const start = performance.now();
    const from = prev.current;
    const delta = target - from;

    if (delta === 0) return;

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const v = from + delta * easeOutCubic(t);
      setValue(t === 1 ? target : v);
      if (t < 1) {
        rafId.current = requestAnimationFrame(step);
      } else {
        prev.current = target;
        rafId.current = null;
      }
    };
    rafId.current = requestAnimationFrame(step);

    return () => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
    };
  }, [target, duration]);

  return value;
}

/**
 * Global keyboard shortcuts. Pass a map of { key: handler }.
 *
 * Skips when:
 *   - 焦点在 INPUT/TEXTAREA/SELECT/contenteditable（含 AntD 包装的 .ant-select 等）
 *   - 任何 AntD Modal 打开
 *   - 任何 AntD Select / Cascader / Picker / Dropdown / Popover 浮层打开
 *   - 任何 .modal-overlay.open（自建 modal）
 *
 * 修 P1-2：浏览器实测 AntD Select 选项点击偶发触发 '0'/'`' 快捷键跳到
 * /dashboard。原因是 dropdown 内点击后焦点会落回非 INPUT 元素，下一次
 * keydown 没被 isEditing 拦下。彻底修法：把任意 AntD 浮层打开都视为"用户
 * 正在操作 UI 元素，不应触发全局快捷键"。
 *
 * Enter / Escape 永远放行（用于 form submit / modal close）。
 */
export function useShortcuts(shortcuts: Record<string, (e: KeyboardEvent) => void>, opts?: { allowInsideModal?: boolean }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const k = normalizeKey(e);
      const isEnter = k === 'enter';
      const isEsc = k === 'escape';
      // 永远不拦 Enter / Esc — 让 modal/form 正常工作
      if (isEnter || isEsc) {
        const fn = shortcuts[k];
        if (fn) { e.preventDefault(); fn(e); }
        return;
      }
      // P1-4：用 document.activeElement 而不是 e.target — 输入态判断要看真实焦点
      if (isInputFocused()) return;
      if (!opts?.allowInsideModal && isAnyModalOpen()) return;
      if (isAnyPopupOpen()) return;

      const fn = shortcuts[k];
      if (fn) {
        e.preventDefault();
        fn(e);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shortcuts, opts?.allowInsideModal]);
}

function normalizeKey(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('ctrl');
  if (e.metaKey) parts.push('meta');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  parts.push(e.key.toLowerCase());
  return parts.join('+');
}

/** 任意输入控件 / AntD 包装的输入控件正处于焦点态。
 *  P1-4 修：以前用 e.target，在某些场景下 e.target 是 body 不是 input。
 *  document.activeElement 是浏览器维护的真实焦点元素，更可靠。 */
function isInputFocused(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el || el === document.body) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  if (el.getAttribute('role') === 'textbox' || el.getAttribute('role') === 'combobox') return true;
  if (typeof el.closest === 'function') {
    if (el.closest(
      '.ant-select, .ant-cascader-picker, .ant-picker, .ant-input-number, ' +
      '.ant-input-affix-wrapper, .ant-mentions, [contenteditable="true"]',
    )) return true;
  }
  return false;
}

/** 任意 AntD modal 或自建 modal 处于打开状态。
 *  AntD 5 关闭 modal 时直接移除 .ant-modal-mask 元素，所以
 *  存在即"打开"，不需要 :not(.*-hidden) 复杂判断。 */
function isAnyModalOpen(): boolean {
  return !!document.querySelector('.modal-overlay.open, .ant-modal-mask');
}

/** 任意 AntD 浮层（Select 下拉 / Cascader / DatePicker / Dropdown / Popover）打开 */
function isAnyPopupOpen(): boolean {
  return !!document.querySelector(
    '.ant-select-dropdown:not(.ant-select-dropdown-hidden), ' +
    '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden), ' +
    '.ant-cascader-dropdown:not(.ant-cascader-dropdown-hidden), ' +
    '.ant-dropdown:not(.ant-dropdown-hidden), ' +
    '.ant-popover:not(.ant-popover-hidden)',
  );
}

/**
 * Watches a list of customers and, whenever a customer transitions to status='dealt'
 * (or stage L7) that we haven't celebrated before, fires confetti + a gold toast.
 *
 * "Already celebrated" is persisted in localStorage so refreshing the page doesn't
 * re-fire the celebration. Returns the set of currently-glowing customer IDs so the
 * caller can apply the `hk-row-deal` class to that row for ~5s.
 */
export function useDealWatcher(customers: Array<any> | undefined) {
  const [glowingIds, setGlowingIds] = useState<Set<number>>(new Set());
  const prevIds = useRef<Set<number>>(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    if (!customers) return;
    const dealtIds = new Set<number>();
    const newGlow = new Set<number>();
    const celebrated: Set<number> = new Set(
      JSON.parse(localStorage.getItem('huanke-celebrated-deals') || '[]'),
    );

    customers.forEach((c) => {
      const isDealt = c.status === 'dealt' || c.currentStage === 'L7' || c.currentStage === 'L8';
      if (isDealt) dealtIds.add(c.id);
    });

    if (!initialized.current) {
      // First render — don't celebrate any historical deals, just snapshot them
      dealtIds.forEach((id) => celebrated.add(id));
      localStorage.setItem('huanke-celebrated-deals', JSON.stringify([...celebrated]));
      prevIds.current = dealtIds;
      initialized.current = true;
      return;
    }

    dealtIds.forEach((id) => {
      if (!celebrated.has(id) && !prevIds.current.has(id)) {
        const c = customers.find((x) => x.id === id);
        if (!c) return;
        fireConfetti();
        const amount = Number(c.totalPaid ?? 0);
        const commission = Math.round(amount * 0.1);
        toast.deal(`🎉「${c.nickname}」成交！¥${amount.toLocaleString()} 计入业绩 · 提成 ¥${commission.toLocaleString()}`);
        newGlow.add(id);
        celebrated.add(id);
      }
    });

    if (newGlow.size > 0) {
      localStorage.setItem('huanke-celebrated-deals', JSON.stringify([...celebrated]));
      setGlowingIds((s) => {
        const next = new Set(s);
        newGlow.forEach((id) => next.add(id));
        return next;
      });
      // Glow fades after 5 seconds
      setTimeout(() => {
        setGlowingIds((s) => {
          const next = new Set(s);
          newGlow.forEach((id) => next.delete(id));
          return next;
        });
      }, 5000);
    }
    prevIds.current = dealtIds;
  }, [customers]);

  return glowingIds;
}
